/**
 * FilterPanel - Panel de filtros para vista de Chats
 * Auto-collapse: se contrae automáticamente y se expande al pasar el mouse
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { foldersAPI, todolistAPI } from '../../services/api'
import useCRMStore from '../../store/useCRMStore'

const COLLAPSE_DELAY = 400

export default function FilterPanel() {
  const activeFilters = useCRMStore(state => state.activeFilters)
  const setFilter = useCRMStore(state => state.setFilter)
  const clearFilters = useCRMStore(state => state.clearFilters)

  // Auto-collapse state
  const [isExpanded, setIsExpanded] = useState(false)
  const collapseTimerRef = useRef(null)

  // Estado para datos
  const [agents, setAgents] = useState([])
  const [labels, setLabels] = useState([])
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)

  // Estado para secciones expandidas
  const [expandedSections, setExpandedSections] = useState({
    agents: true,
    labels: false,
    departments: false
  })

  // Mouse enter solo cancela el cierre pendiente (no auto-expande, requiere click).
  const handleMouseEnter = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current)
      collapseTimerRef.current = null
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    collapseTimerRef.current = setTimeout(() => {
      setIsExpanded(false)
      collapseTimerRef.current = null
    }, COLLAPSE_DELAY)
  }, [])

  // Click expande el panel (no toggle: cierre solo via mouseleave)
  const handleClick = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current)
      collapseTimerRef.current = null
    }
    setIsExpanded(true)
  }, [])

  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current)
    }
  }, [])

  // Cargar datos al montar
  useEffect(() => {
    loadFilterData()
  }, [])

  const loadFilterData = async () => {
    try {
      setLoading(true)
      const [foldersData, labelsData, departmentsData] = await Promise.all([
        foldersAPI.getFolders(),
        todolistAPI.getLabels(),
        todolistAPI.getDepartments()
      ])

      // Extraer agentes de las carpetas de sistema
      const systemFolders = foldersData.filter(f => f.es_sistema)
      setAgents(systemFolders)
      setLabels(labelsData)
      setDepartments(departmentsData)
    } catch (error) {
      console.error('Error cargando filtros:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const handleAgentSelect = (agentName) => {
    setFilter('agent', activeFilters.agent === agentName ? null : agentName)
  }

  const handleLabelSelect = (label) => {
    setFilter('label', activeFilters.label?.id === label.id ? null : label)
  }

  const handleDepartmentSelect = (dept) => {
    setFilter('department', activeFilters.department?.id === dept.id ? null : dept)
  }

  const handleSearchChange = (e) => {
    setFilter('search', e.target.value)
  }

  const handleSortChange = (e) => {
    setFilter('sortOrder', e.target.value)
  }

  const hasActiveFilters = activeFilters.search || activeFilters.agent || activeFilters.label || activeFilters.department
  const activeCount = [activeFilters.agent, activeFilters.label, activeFilters.department].filter(Boolean).length

  return (
      <div
        className={`
          flex-shrink-0 bg-white border-r border-gray-200 overflow-hidden relative
          transition-all duration-300 ease-in-out
          ${isExpanded ? 'w-72 shadow-lg' : 'w-12'}
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={!isExpanded ? handleClick : undefined}
      >
        {/* === Estado colapsado: iconos minimalistas === */}
        <div className={`
          absolute inset-y-0 left-0 w-12 flex flex-col items-center py-4 gap-3 z-10
          transition-opacity duration-200 cursor-pointer
          ${isExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100'}
        `}>
          {/* Icono de filtro */}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
            hasActiveFilters ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'
          }`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </div>

          {/* Badge de filtros activos */}
          {activeCount > 0 && (
            <div className="w-5 h-5 rounded-full bg-primary-500 text-white text-[10px] font-bold flex items-center justify-center">
              {activeCount}
            </div>
          )}

          {/* Indicador agentes */}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
            activeFilters.agent ? 'bg-blue-100' : 'bg-gray-50 hover:bg-gray-100'
          }`} title={activeFilters.agent || 'Agentes'}>
            <span className="text-sm">👥</span>
          </div>

          {/* Indicador etiquetas */}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
            activeFilters.label ? 'bg-green-100' : 'bg-gray-50 hover:bg-gray-100'
          }`} title={activeFilters.label?.name || 'Etiquetas'}>
            <span className="text-sm">🏷️</span>
          </div>

          {/* Indicador departamentos */}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
            activeFilters.department ? 'bg-purple-100' : 'bg-gray-50 hover:bg-gray-100'
          }`} title={activeFilters.department?.name || 'Departamentos'}>
            <span className="text-sm">🏢</span>
          </div>
        </div>

        {/* === Estado expandido: contenido completo === */}
        <div className={`
          w-72 h-full flex flex-col
          transition-opacity duration-300
          ${isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}>
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">Filtros</h2>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-primary-600 hover:text-primary-700"
                  title="Limpiar filtros"
                >
                  Limpiar
                </button>
              )}
            </div>
            <input
              type="text"
              placeholder="Buscar chats..."
              value={activeFilters.search || ''}
              onChange={handleSearchChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />

            {/* Selector de orden */}
            <div className="mt-3">
              <label className="text-xs text-gray-500 mb-1 block">Ordenar por</label>
              <select
                value={activeFilters.sortOrder || 'recent'}
                onChange={handleSortChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              >
                <option value="recent">📅 Más recientes primero</option>
                <option value="oldest">📅 Más antiguos primero</option>
                <option value="name-asc">🔤 Nombre A-Z</option>
                <option value="name-desc">🔤 Nombre Z-A</option>
              </select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <>
                {/* Sección: Agentes */}
                <div
                  className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSection('agents')}
                >
                  <span>👥 Agentes</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">{agents.length}</span>
                    <span>{expandedSections.agents ? '▼' : '▶'}</span>
                  </div>
                </div>
                {expandedSections.agents && (
                  <div className="max-h-64 overflow-y-auto">
                    {agents.map(agent => (
                      <div
                        key={agent.id}
                        onClick={() => handleAgentSelect(agent.nombre)}
                        className={`
                          px-4 py-2 cursor-pointer transition-colors flex items-center gap-2
                          ${activeFilters.agent === agent.nombre ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-50'}
                        `}
                      >
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: agent.color || '#6B7280' }}
                        />
                        <span className="flex-1 text-sm truncate">{agent.nombre}</span>
                        <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                          {agent.contact_count || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Sección: Etiquetas */}
                <div
                  className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSection('labels')}
                >
                  <span>🏷️ Etiquetas</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">{labels.length}</span>
                    <span>{expandedSections.labels ? '▼' : '▶'}</span>
                  </div>
                </div>
                {expandedSections.labels && (
                  <div className="max-h-64 overflow-y-auto">
                    {labels.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No hay etiquetas
                      </div>
                    ) : (
                      labels.map(label => (
                        <div
                          key={label.id}
                          onClick={() => handleLabelSelect(label)}
                          className={`
                            px-4 py-2 cursor-pointer transition-colors flex items-center gap-2
                            ${activeFilters.label?.id === label.id ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-50'}
                          `}
                        >
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: label.color || '#6B7280' }}
                          />
                          <span className="flex-1 text-sm truncate">{label.name}</span>
                          <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                            {label.contact_count || 0}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Sección: Departamentos */}
                <div
                  className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSection('departments')}
                >
                  <span>🏢 Departamentos</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">{departments.length}</span>
                    <span>{expandedSections.departments ? '▼' : '▶'}</span>
                  </div>
                </div>
                {expandedSections.departments && (
                  <div className="max-h-64 overflow-y-auto">
                    {departments.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No hay departamentos
                      </div>
                    ) : (
                      departments.map(dept => (
                        <div
                          key={dept.id}
                          onClick={() => handleDepartmentSelect(dept)}
                          className={`
                            px-4 py-2 cursor-pointer transition-colors flex items-center gap-2
                            ${activeFilters.department?.id === dept.id ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-50'}
                          `}
                        >
                          <span className="w-4 text-center">🏢</span>
                          <span className="flex-1 text-sm truncate">{dept.name}</span>
                          <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                            {dept.contact_count || 0}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Filtros activos - badges */}
          {hasActiveFilters && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-500 mb-2">Filtros activos:</div>
              <div className="flex flex-wrap gap-1">
                {activeFilters.agent && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    👤 {activeFilters.agent}
                    <button onClick={() => setFilter('agent', null)} className="hover:text-blue-600">×</button>
                  </span>
                )}
                {activeFilters.label && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: activeFilters.label.color || '#6B7280' }}
                  >
                    🏷️ {activeFilters.label.name}
                    <button onClick={() => setFilter('label', null)} className="hover:opacity-75">×</button>
                  </span>
                )}
                {activeFilters.department && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    🏢 {activeFilters.department.name}
                    <button onClick={() => setFilter('department', null)} className="hover:text-purple-600">×</button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
  )
}
